import React, { useState, useEffect } from 'react';
import {
    Package, Plus, Trash2, Save, User, Building2, MapPin, FileText,
    AlertCircle, CheckCircle, Loader2, Search, X, ChevronLeft,
    ChevronRight, Warehouse, Clock, CheckSquare, Square, Calendar, Eye
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';

export default function MaterialRequestForm() {
    // Generate MR Number automatically
    const generateMRNo = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');

        return `MR-${year}${month}${day}-${hours}${minutes}${seconds}`;
    };

    const [itemCountId, setItemCountId] = useState(null);

    // 🔥 ASYNC FUNCTION - Generate Control Number
    const generateControlNo = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');

        return `CTRL-${year}-${month}${day}-${hours}${minutes}${seconds}`;
    };

    const [mrHeader, setMrHeader] = useState({
        mrNo: generateMRNo(),
        mrDate: new Date().toISOString().split('T')[0],
        requestedBy: null,
        requestedByName: '',
        arInvoiceNo: '',
        controlNo: '',
        department: '',
        branchSpecify: '',
        locationId: null,
        locationName: '',
        purpose: '',
        remarks: '',
        status: 0,
        createdBy: null,
        requestedBySignature: '',
        approvedBy: null,
        approvedByName: '',
        approvedBySignature: '',
        releasedBy: null,
        releasedByName: '',
        releasedBySignature: ''
    });

    const departmentOptions = [
        'Mechanical', 'QC', 'Accounting', 'Logistics', 'Spareparts',
        'Gen. Services', 'Production', 'RM Whse', 'Packaging', 'Electrical',
        'FG Whse', 'Motorpool', 'Admin/HR/MIS', 'Fabrication', 'Branch'
    ];

    const [mrItems, setMrItems] = useState([]);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [locations, setLocations] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [allMRs, setAllMRs] = useState([]);

    const [itemCountData, setItemCountData] = useState({
        controlNo: '',
        createDate: '',
        currentItemCount: 0,
        maxItemCount: 10
    });

    const [showApprovedItems, setShowApprovedItems] = useState(false);
    const [approvedItemsList, setApprovedItemsList] = useState([]);
    const [loadingApprovedItems, setLoadingApprovedItems] = useState(false);

    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(true);

    const [showItemPicker, setShowItemPicker] = useState(false);
    const [itemSearchText, setItemSearchText] = useState('');
    const [itemCurrentPage, setItemCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // 🔥 NEW: Add loading state for inventory
    const [loadingInventory, setLoadingInventory] = useState(false);
    const [inventoryError, setInventoryError] = useState(null);

    // 🔥 FUNCTION: Fetch Approved Items by Department
    const fetchApprovedItemsByDepartment = async (department) => {
        if (!department || department === 'Branch') return;

        setLoadingApprovedItems(true);
        try {
            // ✅ Use the correct endpoint by department
            const res = await fetch(`${API_BASE_URL}api/MRHeader/by-department/${encodeURIComponent(department)}`);
            if (!res.ok) {
                throw new Error('Failed to fetch MR headers');
            }

            const allMRData = await res.json();

            // ✅ status === 3 = Approved (hindi 2!)
            const approvedMRs = allMRData.filter(mr => mr.status === 3);

            console.log('✅ Found approved MRs:', approvedMRs.length);

            // Fetch MRDetails for each approved MR
            const approvedItems = [];

            for (const mr of approvedMRs) {
                try {
                    const detailsRes = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${mr.id}`);

                    if (detailsRes.ok) {
                        const details = await detailsRes.json();

                        details.forEach(detail => {
                            approvedItems.push({
                                ...detail,
                                mrNo: mr.mrNo,
                                controlNo: mr.controlNo,
                                mrDate: mr.mrDate,
                                requestedByName: mr.requestedByName,
                                approvedByName: mr.approvedByName,
                                approvedDate: mr.approvedDate   // ✅ DAGDAG: approvedDate
                            });
                        });
                    }
                } catch (err) {
                    console.error(`Failed to fetch details for MR ${mr.id}:`, err);
                }
            }

            console.log('✅ Approved Items Found:', approvedItems.length);
            setApprovedItemsList(approvedItems);
            setShowApprovedItems(true);

            if (approvedItems.length === 0) {
                Swal.fire({
                    icon: 'info',
                    title: 'No Approved Items',
                    text: `No approved items found for ${department}`,
                    confirmButtonColor: '#1e40af'
                });
            }

        } catch (error) {
            console.error('❌ Error fetching approved items:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load approved items',
                confirmButtonColor: '#dc2626'
            });
        } finally {
            setLoadingApprovedItems(false);
        }
    };

    // 🔥 FUNCTION: Fetch Item Count by Department
    // 🔥 UPDATE: Add mrNo parameter
    // 🔥 UPDATED: Simplified - Use Backend Logic Only
    const fetchItemCountByDepartment = async (department, mrNo) => {
        if (!department || department === 'Branch') return;

        try {
            const res = await fetch(
                `${API_BASE_URL}api/GoodIssue_ItemCount/get-or-create-control/${encodeURIComponent(department)}`
            );

            if (res.ok) {
                const data = await res.json();

                setItemCountData({
                    controlNo: data.controlNo,
                    createDate: new Date().toISOString(),
                    currentItemCount: data.currentCount,
                    maxItemCount: data.maxCount
                });

                setMrHeader(prev => ({
                    ...prev,
                    controlNo: data.controlNo
                }));

                console.log(`✅ Control No: ${data.controlNo} (${data.currentCount}/10) - Status: ${data.status}`);

                // Show notification if NEW control number was created
                if (data.status === 'new') {
                    Swal.fire({
                        icon: 'info',
                        title: 'New Control Number',
                        html: `<strong>${data.controlNo}</strong><br/>Previous batch was full (10/10)`,
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            }
        } catch (error) {
            console.error('❌ Error fetching control number:', error);
        }
    };

    const updateItemCount = async (controlNo, department, itemCount) => {
        try {
            const res = await fetch(
                `${API_BASE_URL}api/GoodIssue_ItemCount?controlNo=${controlNo}&department=${encodeURIComponent(department)}`
            );

            if (res.ok) {
                const existingRecords = await res.json();

                if (existingRecords && existingRecords.length > 0) {
                    const existing = existingRecords[0];
                    const updateResponse = await fetch(
                        `${API_BASE_URL}api/GoodIssue_ItemCount/${existing.id}`,
                        {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                ...existing,
                                createItemCount: existing.createItemCount + itemCount,
                                mrNo: mrHeader.mrNo  // ✅ This should now have the correct value
                            })
                        }
                    );

                    if (updateResponse.ok) {
                        console.log('✅ Item count updated');
                    }
                }
            }
        } catch (error) {
            console.error('Error updating item count:', error);
        }
    };

    // 🔥 FUNCTION: CREATE GoodIssue_ItemCount
    const createItemCount = async (controlNo, department, itemCount) => {
        try {
            const payload = {
                mrNo: mrHeader.mrNo,
                controlNo: controlNo,
                department: department,
                createItemCount: itemCount,
                createDate: new Date().toISOString()
            };

            const response = await fetch(`${API_BASE_URL}api/GoodIssue_ItemCount`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const result = await response.json();
                console.log('✅ Item count created:', result);
                setItemCountId(result.data?.id);
            }
        } catch (error) {
            console.error('Error creating item count:', error);
        }
    };

    // 🔥 SINGLE useEffect - Initialize Form
    useEffect(() => {
        const initializeForm = async () => {
            const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
            const initialControlNo = generateControlNo();

            setMrHeader(prev => ({
                ...prev,
                controlNo: initialControlNo,
                requestedBy: currentUser.userId || 1,
                requestedByName: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || 'Current User',
                createdBy: currentUser.userId || 1
            }));
        };

        fetchReferenceData();
        initializeForm();
    }, []);

    const fetchReferenceData = async () => {
        setLoadingData(true);
        setInventoryError(null);

        try {
            console.log('🔄 Fetching reference data from:', API_BASE_URL);

            const [invRes, locRes, whRes, mrRes, itemsRes] = await Promise.all([
                fetch(`${API_BASE_URL}api/InventoryData`).catch(err => {
                    console.error('❌ Inventory API failed:', err);
                    return null;
                }),

                fetch(`${API_BASE_URL}api/WareHouse`).catch(() => null),
                fetch(`${API_BASE_URL}api/MR_Header`).catch(() => null),
                fetch(`${API_BASE_URL}api/Items`).catch(() => null)  // 🔥 Items API
            ]);

            let invData = [];
            let locData = [];
            let whData = [];
            let mrData = [];
            let itemsData = [];

            // STEP 1: Parse Items
            if (itemsRes && itemsRes.ok) {
                try {
                    const data = await itemsRes.json();
                    itemsData = Array.isArray(data) ? data : (data ? [data] : []);
                    console.log('✅ Items loaded:', itemsData.length);
                } catch (e) {
                    console.warn('Failed to parse Items:', e);
                }
            }

            // STEP 2: Parse Inventory
            if (invRes && invRes.ok) {
                try {
                    const data = await invRes.json();
                    invData = Array.isArray(data) ? data : (data ? [data] : []);
                    console.log('✅ Inventory items loaded:', invData.length);

                    if (invData.length === 0) {
                        console.warn('⚠️ No inventory items found in API response');
                        setInventoryError('No inventory items available. Please add items first.');
                    }
                } catch (e) {
                    console.error('❌ Failed to parse InventoryData:', e);
                    setInventoryError('Failed to load inventory data');
                }
            } else {
                console.error('❌ Inventory API returned error:', invRes?.status);
                setInventoryError('Failed to connect to inventory API');
            }

            // STEP 3: Merge category into invData AFTER both are loaded
            if (itemsData.length > 0 && invData.length > 0) {
                const itemsMap = {};
                itemsData.forEach(item => {
                    itemsMap[item.itemCode] = item;
                });

                invData = invData.map(invItem => ({
                    ...invItem,
                    category: itemsMap[invItem.itemCode]?.category || 'Uncategorized'
                }));

                console.log('✅ Categories merged into inventory');
            }

            // STEP 4: Parse the rest
            if (locRes && locRes.ok) {
                try {
                    const data = await locRes.json();
                    locData = Array.isArray(data) ? data : (data ? [data] : []);
                    console.log('✅ Locations loaded:', locData.length);
                } catch (e) {
                    console.warn('Failed to parse Location:', e);
                }
            }

            if (whRes && whRes.ok) {
                try {
                    const data = await whRes.json();
                    whData = Array.isArray(data) ? data : (data ? [data] : []);
                    console.log('✅ Warehouses loaded:', whData.length);
                } catch (e) {
                    console.warn('Failed to parse WareHouse:', e);
                }
            }

            if (mrRes && mrRes.ok) {
                try {
                    const data = await mrRes.json();
                    mrData = Array.isArray(data) ? data : (data ? [data] : []);
                    setAllMRs(mrData);
                    console.log('✅ MR Headers loaded:', mrData.length);
                } catch (e) {
                    console.warn('Failed to parse MR_Header:', e);
                }
            }

            // Group by itemCode — isang row lang per item sa picker
            // 🔥 FIX: Filter out useItems === "Yes" BEFORE grouping
            const validInvData = invData.filter(inv => inv.useItems !== "Yes");

            // Group by itemCode — isang row lang per item sa picker
            const groupMap = new Map();
            validInvData.forEach(inv => {
                const key = inv.itemCode;
                if (!groupMap.has(key)) {
                    groupMap.set(key, {
                        ...inv,
                        availableQty: inv.availableQty || 0,
                        quantity: inv.quantity || 0,
                    });
                } else {
                    const existing = groupMap.get(key);
                    existing.availableQty += (inv.availableQty || 0);
                    existing.quantity += (inv.quantity || 0);
                }
            });
            setInventoryItems(Array.from(groupMap.values()));
            setLocations(locData);
            setWarehouses(whData);

            console.log('📦 Reference Data Summary:', {
                inventory: invData.length,
                locations: locData.length,
                warehouses: whData.length,
                mrs: mrData.length
            });

        } catch (error) {
            console.error('❌ Error fetching reference data:', error);
            setInventoryError('Unexpected error loading data');
        } finally {
            setLoadingData(false);
        }
    };

    // 🔥 ADD: Category state
    const [selectedCategory, setSelectedCategory] = useState('All');

    // 🔥 ADD: Extract unique categories from inventory
    const categories = ['All', ...new Set(
        inventoryItems
            .map(item => item.category || 'Uncategorized')
            .filter(Boolean)
    )];

    // 🔥 UPDATE: Filter function with category
    const filteredInventoryItems = inventoryItems.filter(item => {
        // 🔥 Category filter
        if (selectedCategory !== 'All') {
            const itemCategory = item.category || 'Uncategorized';
            if (itemCategory !== selectedCategory) return false;
        }

        // Search filter
        if (itemSearchText.trim()) {
            const search = itemSearchText.toLowerCase();
            const matchesSearch = (
                item.itemCode?.toLowerCase().includes(search) ||
                item.itemDescription?.toLowerCase().includes(search) ||
                item.warehouseCode?.toLowerCase().includes(search)
            );
            if (!matchesSearch) return false;
        }

        // Already added filter
        const alreadyInForm = mrItems.some(mrItem => mrItem.inventoryDataId === item.id);
        if (alreadyInForm) return false;

        // UseItems filter
        if (item.useItems === "Yes") return false;

        return true;
    });

    const totalItemPages = Math.ceil(filteredInventoryItems.length / itemsPerPage);
    const startItemIndex = (itemCurrentPage - 1) * itemsPerPage;
    const endItemIndex = startItemIndex + itemsPerPage;

    const currentInventoryItems = filteredInventoryItems.slice(startItemIndex, endItemIndex);

    const handleSelectItem = (invItem) => {
        console.log('🔥 SELECTED ITEM:', {
            id: invItem.id,
            itemCode: invItem.itemCode,
            availableQty: invItem.availableQty,
        });

        const exists = mrItems.find(item => item.inventoryDataId === invItem.id);
        if (exists) {
            Swal.fire({
                icon: 'warning',
                title: 'Item Already Added',
                text: `This item is already in your request list!`,
                confirmButtonColor: '#f59e0b'
            });
            return;
        }

        if (invItem.availableQty <= 0) {
            Swal.fire({
                icon: 'error',
                title: 'No Available Stock',
                text: `${invItem.itemCode} has no available quantity!`,
                confirmButtonColor: '#dc2626'
            });
            return;
        }

        const newItem = {
            id: Date.now(),
            inventoryDataId: invItem.id,
            itemCode: invItem.itemCode,
            itemDescription: invItem.itemDescription,
            requestedQty: 1,
            maxAvailable: invItem.availableQty,
            issuedQty: 0,
            uom: invItem.uom || 'pcs',
            warehouseCode: invItem.warehouseCode,
            warehouseId: invItem.warehouseId,
            batchNo: invItem.batchNo || '',
            purpose: '',
            remarks: '',
            status: 'Pending',
            availableQty: invItem.availableQty || 0,
            quantity: invItem.quantity || 0,
            reservedQty: invItem.reservedQty || 0
        };

        console.log('✅ NEW ITEM ADDED:', newItem);
        setMrItems([...mrItems, newItem]);

        Swal.fire({
            icon: 'success',
            title: 'Item Added!',
            html: `<strong>${invItem.itemCode}</strong><br/>Available: ${invItem.availableQty} ${invItem.uom}`,
            timer: 1500,
            showConfirmButton: false
        });
    };

    const removeItem = (id) => {
        setMrItems(mrItems.filter(item => item.id !== id));
    };

    const updateItem = (id, field, value) => {
        setMrItems(mrItems.map(item => {
            if (item.id === id) {
                if (field === 'requestedQty') {
                    const qty = parseFloat(value) || 0;

                    if (qty > item.maxAvailable) {
                        Swal.fire({
                            icon: 'warning',
                            title: 'Insufficient Stock!',
                            html: `Only <strong>${item.maxAvailable} ${item.uom}</strong> available for <strong>${item.itemCode}</strong>`,
                            timer: 2000,
                            showConfirmButton: false
                        });
                    }

                    console.log('📦 Qty Updated:', {
                        itemCode: item.itemCode,
                        requested: qty,
                        available: item.maxAvailable
                    });
                }

                return { ...item, [field]: value };
            }
            return item;
        }));
    };

    const handleLocationChange = (locId) => {
        const loc = locations.find(l => l.id === parseInt(locId));
        setMrHeader({
            ...mrHeader,
            locationId: loc?.id || null,
            locationName: loc?.locationName || ''
        });
    };

    const fetchControlNumberByDepartment = async (department) => {
        if (!department || department === 'Branch') return;

        try {
            const res = await fetch(
                `${API_BASE_URL}api/GoodIssue_ItemCount/get-or-create-control/${encodeURIComponent(department)}`
            );

            if (res.ok) {
                const data = await res.json();

                console.log('✅ Control No for', department, ':', data.controlNo);
                console.log('📊 Current Count:', data.currentCount, '/', data.maxCount);

                setItemCountData({
                    controlNo: data.controlNo,
                    createDate: new Date().toISOString(),
                    currentItemCount: data.currentCount,
                    maxItemCount: data.maxCount
                });

                setMrHeader(prev => ({
                    ...prev,
                    controlNo: data.controlNo
                }));

                // Show alert if it's a new Control No
                if (data.status === 'new') {
                    Swal.fire({
                        icon: 'success',
                        title: 'New Control Number!',
                        html: `<strong>${data.controlNo}</strong><br/>Previous batch was full (10/10)`,
                        timer: 2000,
                        showConfirmButton: false
                    });
                }
            }
        } catch (error) {
            console.error('❌ Error fetching control number:', error);
        }
    };

    // Replace existing handleDepartmentToggle with:

    // Replace existing fetchItemCountByDepartment call with:
    // 🔥 SUPER SIMPLE - WALANG API CALL!
    // 🔥 AYUSIN - Pag click ng department, agad kuha ng control number!
    const handleDepartmentToggle = async (dept) => {
        const newDept = mrHeader.department === dept ? '' : dept;

        // Generate bagong control no based sa timestamp
        const newControlNo = newDept ? generateControlNo() : '';

        setMrHeader(prev => ({
            ...prev,
            department: newDept,
            controlNo: newControlNo
        }));

        // Fetch item count data para sa display lang (hindi na mag-set ng controlNo)
        if (newDept && newDept !== 'Branch') {
            try {
                const res = await fetch(
                    `${API_BASE_URL}api/GoodIssue_ItemCount/get-or-create-control/${encodeURIComponent(newDept)}`
                );
                if (res.ok) {
                    const data = await res.json();
                    setItemCountData({
                        controlNo: newControlNo, // ← timestamp-based na
                        createDate: new Date().toISOString(),
                        currentItemCount: data.currentCount,
                        maxItemCount: data.maxCount
                    });
                }
            } catch (error) {
                console.error('❌ Error:', error);
            }
        } else {
            setItemCountData({
                controlNo: '',
                createDate: '',
                currentItemCount: 0,
                maxItemCount: 10
            });
        }
    };

    const saveMR = async (submitForApproval = false) => {
        setLoading(true);

        try {
            // 🔥 VALIDATION: Check if department is selected
            if (!mrHeader.department) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Department Required',
                    text: 'Please select a department first!',
                    confirmButtonColor: '#f59e0b'
                });
                setLoading(false);
                return;
            }

            // 🔥 VALIDATION: Check if items exist
            if (mrItems.length === 0) {
                Swal.fire({
                    icon: 'warning',
                    title: 'No Items',
                    text: 'Please add at least one item!',
                    confirmButtonColor: '#f59e0b'
                });
                setLoading(false);
                return;
            }

            // 🔥 STEP 1: CREATE ItemCount (backend auto-calculates control number!)
            const itemCountPayload = {
                mrNo: mrHeader.mrNo,
                controlNo: mrHeader.controlNo, // ✅ Use from state (backend will recalculate if needed)
                department: mrHeader.department === 'Branch'
                    ? `Branch - ${mrHeader.branchSpecify}`
                    : mrHeader.department,
                createItemCount: mrItems.length,
                createDate: new Date().toISOString()
            };

            console.log('📤 Sending ItemCount:', itemCountPayload);

            const itemCountRes = await fetch(`${API_BASE_URL}api/GoodIssue_ItemCount`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(itemCountPayload)
            });

            if (!itemCountRes.ok) {
                throw new Error('Failed to save item count');
            }

            const itemCountResult = await itemCountRes.json();
            console.log('✅ ItemCount Response:', itemCountResult);

            // ✅ Get ACTUAL control number from backend response
            const actualControlNo = mrHeader.controlNo; // ← sarili nating format, ignore backend
            const status = itemCountResult.status;

            console.log(`✅ Using Control No: ${actualControlNo} (${status})`);

            // 🔥 STEP 2: Save MR Header
            const mrHeaderData = {
                mrNo: mrHeader.mrNo,
                mrDate: new Date(mrHeader.mrDate).toISOString(),
                requestedBy: mrHeader.requestedBy,
                requestedByName: mrHeader.requestedByName,
                arInvoiceNo: mrHeader.arInvoiceNo || '',
                controlNo: actualControlNo,  // ✅ Use backend-returned control number
                department: mrHeader.department,
                departmentId: 999,
                departmentName: mrHeader.department === 'Branch'
                    ? `Branch - ${mrHeader.branchSpecify}`
                    : mrHeader.department,
                branchSpecify: mrHeader.branchSpecify || '',
                locationId: mrHeader.locationId,
                locationName: mrHeader.locationName,
                purpose: mrHeader.purpose || '',
                remarks: mrHeader.remarks || '',
                status: submitForApproval ? 1 : 0,
                createdBy: mrHeader.createdBy,
                createdDate: new Date().toISOString(),
                requestedBySignature: mrHeader.requestedBySignature || '',
                approvedBy: mrHeader.approvedBy,
                approvedByName: mrHeader.approvedByName || '',
                approvedBySignature: mrHeader.approvedBySignature || '',
                releasedBy: mrHeader.releasedBy,
                releasedByName: mrHeader.releasedByName || '',
                releasedBySignature: mrHeader.releasedBySignature || ''

            };

            console.log('📤 Saving MR Header:', mrHeaderData);

            const headerResponse = await fetch(`${API_BASE_URL}api/MRHeader`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(mrHeaderData)
            });

            if (!headerResponse.ok) {
                const errorText = await headerResponse.text();
                throw new Error(`Failed to save MR Header: ${errorText}`);
            }

            const headerResult = await headerResponse.json();
            const mrHeaderId = headerResult.data?.id || headerResult.id;

            console.log('✅ MR Header saved, ID:', mrHeaderId);

            // 🔥 STEP 3: Save MR Details
            for (let i = 0; i < mrItems.length; i++) {
                const item = mrItems[i];

                const mrDetailData = {
                    id: 0,
                    mrHeaderId: mrHeaderId,
                    itemCode: item.itemCode,
                    itemDescription: item.itemDescription,
                    requestedQty: parseFloat(item.requestedQty),
                    issuedQty: 0,
                    uom: item.uom || 'pcs',
                    warehouseCode: item.warehouseCode || '',
                    warehouseId: item.warehouseId,
                    batchNo: item.batchNo || '',
                    remarks: item.remarks || '',
                    status: 'Pending',
                    purpose: item.purpose || '',
                    inventoryDataId: item.inventoryDataId
                };

                const detailResponse = await fetch(`${API_BASE_URL}api/MRDetails`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': '*/*'
                    },
                    body: JSON.stringify(mrDetailData)
                });

                if (!detailResponse.ok) {
                    throw new Error(`Failed to save item ${item.itemCode}`);
                }
            }

            console.log('✅ All MR Details saved!');


            const user = JSON.parse(localStorage.getItem("user"));
            await fetch(`${API_BASE_URL}api/Logs`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    action: "CREATE",
                    module: "Item Request",
                    description: `MR ${mrHeader.mrNo} ${submitForApproval ? 'submitted for approval' : 'saved as draft'}. Control No: ${actualControlNo}, Dept: ${mrHeader.department}, Items: ${mrItems.length}`,
                    userId: user?.userId,
                    username: user?.username,
                    fullName: user?.fullName || user?.username,
                })
            }).catch(err => console.error("Log failed:", err));
            // 🔥 Success message
            Swal.fire({
                icon: 'success',
                title: submitForApproval ? 'MR Submitted!' : 'MR Saved as Draft!',
                html: `
                <strong>${mrHeader.mrNo}</strong><br>
                Control No: <strong>${actualControlNo}</strong><br>
                Department: <strong>${mrHeader.department}</strong><br>
                ${mrItems.length} items
            `,
                confirmButtonColor: '#1e40af',
                timer: 3000
            });

            await resetForm();

        } catch (error) {
            console.error('❌ ERROR:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error Saving MR',
                text: error.message,
                confirmButtonColor: '#dc2626'
            });
        } finally {
            setLoading(false);
        }
    };

    const resetForm = async () => {
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const savedDepartment = mrHeader.department;

        setMrItems([]);

        const newMRNo = generateMRNo();  // ✅ Generate first

        if (savedDepartment && savedDepartment !== 'Branch') {
            await fetchItemCountByDepartment(savedDepartment, newMRNo);  // ✅ Pass the new MRNo!


            setMrHeader(prev => ({
                ...prev,
                mrNo: newMRNo,
                mrDate: new Date().toISOString().split('T')[0],
                arInvoiceNo: '',
                purpose: '',
                remarks: '',
                locationId: null,
                locationName: '',
                branchSpecify: ''
            }));
        } else {
            const newControlNo = generateControlNo();

            setMrHeader({
                mrNo: newMRNo,
                mrDate: new Date().toISOString().split('T')[0],
                requestedBy: currentUser.userId || 1,
                requestedByName: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || 'Current User',
                arInvoiceNo: '',
                controlNo: newControlNo,
                department: savedDepartment,
                branchSpecify: '',
                locationId: null,
                locationName: '',
                purpose: '',
                remarks: '',
                status: 0,
                createdBy: currentUser.userId || 1,
                requestedBySignature: '',
                approvedBy: null,
                approvedByName: '',
                approvedBySignature: '',
                releasedBy: null,
                releasedByName: '',
                releasedBySignature: ''
            });
        }
    };

    // 🔥 NEW: Function to manually retry loading inventory
    const retryLoadInventory = () => {
        fetchReferenceData();
    };

    if (loadingData) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">Loading form data...</p>
                    <p className="text-gray-400 text-sm mt-2">Please wait...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 p-6">
            <div className="max-w-7xl mx-auto">

                {/* Header */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
                    <div className="flex items-center gap-4">
                        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-xl shadow-lg shadow-blue-200">
                            <FileText className="w-8 h-8 text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Spare Parts Item Request Form</h1>
                            <p className="text-gray-500 mt-1">Request materials from inventory for your department</p>
                        </div>
                    </div>
                </div>

                {/* 🔥 NEW: Show error message if inventory failed to load */}
                {inventoryError && (
                    <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-5 mb-6">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                <p className="text-sm font-semibold text-red-800">{inventoryError}</p>
                                <p className="text-xs text-red-600 mt-1">
                                    Check your API connection: <code className="bg-red-100 px-2 py-0.5 rounded">{API_BASE_URL}api/MR_Header/inventory-availability</code>
                                </p>
                            </div>
                            <button
                                onClick={retryLoadInventory}
                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2"
                            >
                                <Loader2 className="w-4 h-4" />
                                Retry
                            </button>
                        </div>
                    </div>
                )}

                {/* MR Header Form */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <div className="w-1 h-6 bg-blue-600 rounded-full"></div>
                        Request Information
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Date *</label>
                            <input
                                type="date"
                                value={mrHeader.mrDate}
                                onChange={(e) => setMrHeader({ ...mrHeader, mrDate: e.target.value })}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Name (Requested By)</label>
                            <input
                                type="text"
                                value={mrHeader.requestedByName}
                                readOnly
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-600"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">A/R Invoice No.</label>
                            <input
                                type="text"
                                value={mrHeader.arInvoiceNo}
                                onChange={(e) => setMrHeader({ ...mrHeader, arInvoiceNo: e.target.value })}
                                placeholder="AR-12345"
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                Control No.
                                {itemCountData.currentItemCount > 0 && mrHeader.department && mrHeader.department !== 'Branch' && (
                                    <span className="ml-auto text-xs font-bold bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                                        {itemCountData.currentItemCount} items today
                                    </span>
                                )}
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={mrHeader.controlNo}
                                    readOnly
                                    className="w-full px-4 py-3 border border-blue-200 rounded-xl bg-blue-50 text-blue-900 font-mono font-bold pr-12"
                                />
                                {itemCountData.createDate && (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2 group">
                                        <Calendar className="w-5 h-5 text-blue-600" />
                                        <div className="absolute right-0 top-full mt-2 hidden group-hover:block bg-gray-900 text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap z-10">
                                            {new Date(itemCountData.createDate).toLocaleDateString()}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Department Checkboxes */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-3">
                            <label className="block text-sm font-semibold text-gray-700">Department *</label>

                            {mrHeader.department && mrHeader.department !== 'Branch' && (
                                <button
                                    onClick={() => fetchApprovedItemsByDepartment(mrHeader.department)}
                                    disabled={loadingApprovedItems}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-all shadow-md disabled:opacity-50"
                                >
                                    {loadingApprovedItems ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            <Eye className="w-4 h-4" />
                                            View Approved Items
                                        </>
                                    )}
                                </button>
                            )}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                            {departmentOptions.map(dept => (
                                <div
                                    key={dept}
                                    onClick={() => handleDepartmentToggle(dept)}
                                    className={`flex items-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-all ${mrHeader.department === dept
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                                        }`}
                                >
                                    {mrHeader.department === dept ? (
                                        <CheckSquare className="w-5 h-5 text-blue-600" />
                                    ) : (
                                        <Square className="w-5 h-5 text-gray-400" />
                                    )}
                                    <span className={`text-sm font-medium ${mrHeader.department === dept ? 'text-blue-900' : 'text-gray-700'
                                        }`}>
                                        {dept}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {mrHeader.department === 'Branch' && (
                            <div className="mt-4">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Branch Specify *</label>
                                <input
                                    type="text"
                                    value={mrHeader.branchSpecify}
                                    onChange={(e) => setMrHeader({ ...mrHeader, branchSpecify: e.target.value })}
                                    placeholder="Enter branch name..."
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                />
                            </div>
                        )}
                    </div>


                    <div className="mt-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Remarks</label>
                        <textarea
                            value={mrHeader.remarks}
                            onChange={(e) => setMrHeader({ ...mrHeader, remarks: e.target.value })}
                            placeholder="Additional notes..."
                            rows={3}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
                        />
                    </div>
                </div>

                {/* MR Items Section */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                            <div className="w-1 h-6 bg-blue-600 rounded-full"></div>
                            Requested Items
                            <span className="bg-blue-100 text-blue-700 text-sm font-bold px-3 py-1 rounded-full">
                                {mrItems.length}
                            </span>
                        </h2>

                        {/* 🔥 IMPROVED: Better button with loading state and error handling */}
                        <div className="flex items-center gap-3">
                            {inventoryItems.length > 0 && (
                                <span className="text-sm text-gray-500">
                                    {inventoryItems.filter(item => item.availableQty > 0).length} items available
                                </span>
                            )}
                            <button
                                onClick={() => {
                                    console.log('🔥 Add Item clicked');
                                    console.log('📦 Inventory items:', inventoryItems.length);
                                    setShowItemPicker(true);
                                }}
                                disabled={inventoryItems.length === 0}
                                className={`px-6 py-3 rounded-xl font-semibold transition-all shadow-lg flex items-center gap-2 ${inventoryItems.length === 0
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-xl hover:shadow-blue-300 shadow-blue-200'
                                    }`}
                                title={inventoryItems.length === 0 ? 'No inventory items available - check API connection' : 'Add item from inventory'}
                            >
                                <Plus className="w-5 h-5" />
                                Add Item {inventoryItems.length === 0 && '(No Stock)'}
                            </button>
                        </div>
                    </div>

                    {inventoryItems.length === 0 && mrItems.length === 0 && (
                        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-xl p-5 mb-4">
                            <p className="text-sm text-amber-800 flex items-center gap-2">
                                <AlertCircle className="w-5 h-5" />
                                <strong>No inventory items available.</strong>
                                {inventoryError ? ' API Error - check connection.' : ' Please add items to your inventory first.'}
                            </p>
                            {inventoryError && (
                                <button
                                    onClick={retryLoadInventory}
                                    className="mt-3 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2"
                                >
                                    <Loader2 className="w-4 h-4" />
                                    Retry Loading
                                </button>
                            )}
                        </div>
                    )}

                    {mrItems.length === 0 ? (
                        <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500 text-lg font-medium">No items added yet</p>
                            <p className="text-gray-400 text-sm mt-2">
                                {inventoryItems.length === 0
                                    ? 'Load inventory items first'
                                    : 'Click "Add Item" to start building your request'
                                }
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-hidden rounded-xl border border-gray-200">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200">
                                            <th className="px-4 py-4 text-left text-xs font-bold text-blue-900 uppercase tracking-wider">Item Code</th>
                                            <th className="px-4 py-4 text-center text-xs font-bold text-blue-900 uppercase tracking-wider">Qty.</th>
                                            <th className="px-4 py-4 text-left text-xs font-bold text-blue-900 uppercase tracking-wider">UOM</th>
                                            <th className="px-4 py-4 text-left text-xs font-bold text-blue-900 uppercase tracking-wider">Item Description</th>
                                            <th className="px-4 py-4 text-left text-xs font-bold text-blue-900 uppercase tracking-wider">Purpose</th>
                                            <th className="px-4 py-4 text-center text-xs font-bold text-blue-900 uppercase tracking-wider">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {mrItems.map((item, index) => {
                                            const isInsufficient = item.requestedQty > item.availableQty;

                                            return (
                                                <tr key={item.id} className="hover:bg-blue-50 transition-colors">
                                                    <td className="px-4 py-4">
                                                        <span className="font-mono font-bold text-blue-900">{item.itemCode}</span>
                                                        <div className="flex flex-col gap-1 mt-2">
                                                            <div className="text-xs text-gray-500">
                                                                📦 WH: <span className="font-semibold">{item.warehouseCode}</span>
                                                            </div>
                                                            <div className="flex items-center gap-2 text-xs">
                                                                <span className="text-gray-500">Available:</span>
                                                                <span className={`font-bold ${item.maxAvailable > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                                    {item.maxAvailable}
                                                                </span>
                                                                <span className="text-gray-400">/ {item.quantity}</span>
                                                            </div>
                                                            {item.reservedQty > 0 && (
                                                                <div className="text-xs text-amber-600">
                                                                    🔒 Reserved: <span className="font-bold">{item.reservedQty}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <div className="flex flex-col items-center gap-1">
                                                            <div className="relative">
                                                                <input
                                                                    type="number"
                                                                    value={item.requestedQty}
                                                                    onChange={(e) => updateItem(item.id, 'requestedQty', e.target.value)}
                                                                    min="1"
                                                                    max={item.maxAvailable}
                                                                    className={`w-24 px-3 py-2 border rounded-lg text-center font-semibold transition-all ${isInsufficient
                                                                        ? 'border-red-500 bg-red-50 text-red-700 focus:ring-2 focus:ring-red-500'
                                                                        : 'border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                                                                        }`}
                                                                />
                                                                <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap">
                                                                    <span className={`text-xs font-semibold ${isInsufficient ? 'text-red-600' : 'text-gray-500'}`}>
                                                                        max: {item.maxAvailable}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {isInsufficient && (
                                                                <span className="mt-4 text-xs text-red-600 flex items-center gap-1 bg-red-100 px-2 py-1 rounded-full">
                                                                    <AlertCircle className="w-3 h-3" /> Exceeds Stock!
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <span className="text-sm font-medium text-gray-700">{item.uom}</span>
                                                    </td>
                                                    <td className="px-4 py-4 text-sm text-gray-700">{item.itemDescription}</td>
                                                    <td className="px-4 py-4">
                                                        <input
                                                            type="text"
                                                            value={item.purpose}
                                                            onChange={(e) => updateItem(item.id, 'purpose', e.target.value)}
                                                            placeholder="Purpose..."
                                                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <div className="flex justify-center">
                                                            <button
                                                                onClick={() => removeItem(item.id)}
                                                                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-all shadow-sm hover:shadow-md flex items-center gap-1"
                                                                title="Remove item"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Approval/Signature Section */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <div className="w-1 h-6 bg-blue-600 rounded-full"></div>
                        Approval & Release
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="border-2 border-gray-200 rounded-xl p-5 bg-gray-50">
                            <label className="block text-sm font-bold text-gray-700 mb-3">Requested By:</label>
                            <input
                                type="text"
                                value={mrHeader.requestedByName}
                                readOnly
                                className="w-full px-3 py-2 mb-3 border border-gray-200 rounded-lg bg-white text-sm font-medium"
                            />
                            <div className="text-xs text-gray-500 italic">Requestor's Signature & Date</div>
                            <div className="mt-2 h-16 border-2 border-dashed border-gray-300 rounded-lg bg-white flex items-center justify-center">
                                <User className="w-6 h-6 text-gray-300" />
                            </div>
                        </div>

                        <div className="border-2 border-gray-200 rounded-xl p-5 bg-gray-50">
                            <label className="block text-sm font-bold text-gray-700 mb-3">Approved By:</label>
                            <input
                                type="text"
                                value={mrHeader.approvedByName}
                                onChange={(e) => setMrHeader({ ...mrHeader, approvedByName: e.target.value })}
                                placeholder="Supervisor/Immediate Supervisor"
                                className="w-full px-3 py-2 mb-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="text-xs text-gray-500 italic">Signature & Date</div>
                            <div className="mt-2 h-16 border-2 border-dashed border-gray-300 rounded-lg bg-white flex items-center justify-center">
                                <CheckCircle className="w-6 h-6 text-gray-300" />
                            </div>
                        </div>

                        <div className="border-2 border-gray-200 rounded-xl p-5 bg-gray-50">
                            <label className="block text-sm font-bold text-gray-700 mb-3">Released By:</label>
                            <input
                                type="text"
                                value={mrHeader.releasedByName}
                                onChange={(e) => setMrHeader({ ...mrHeader, releasedByName: e.target.value })}
                                placeholder="Spare Parts Personnel's"
                                className="w-full px-3 py-2 mb-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="text-xs text-gray-500 italic">Signature & Date</div>
                            <div className="mt-2 h-16 border-2 border-dashed border-gray-300 rounded-lg bg-white flex items-center justify-center">
                                <Package className="w-6 h-6 text-gray-300" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-4">
                    <button
                        onClick={() => saveMR(false)}
                        disabled={loading}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-4 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="w-5 h-5" />
                                Save as Draft
                            </>
                        )}
                    </button>

                    <button
                        onClick={() => saveMR(true)}
                        disabled={loading}
                        className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-xl font-bold transition-all shadow-lg shadow-blue-200 hover:shadow-xl hover:shadow-blue-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Submitting...
                            </>
                        ) : (
                            <>
                                <CheckCircle className="w-5 h-5" />
                                Submit for Approval
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* APPROVED ITEMS MODAL */}
            {showApprovedItems && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col animate-slideUp">

                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="bg-white/20 p-2 rounded-xl">
                                        <CheckCircle className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold">Approved Items - {mrHeader.department}</h3>
                                        <p className="text-green-100 text-sm mt-1">
                                            {approvedItemsList.length} approved items found
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowApprovedItems(false)}
                                    className="bg-white/10 hover:bg-white/20 rounded-xl p-2 transition-all"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {approvedItemsList.length === 0 ? (
                                <div className="text-center py-20">
                                    <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500 text-lg font-medium">No approved items found</p>
                                </div>
                            ) : (
                                <div className="overflow-hidden rounded-xl border border-gray-200">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-gradient-to-r from-green-50 to-green-100 border-b border-green-200">
                                                <th className="px-4 py-3 text-left text-xs font-bold text-green-900 uppercase">MR No.</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-green-900 uppercase">Control No.</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-green-900 uppercase">Item Code</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-green-900 uppercase">Description</th>
                                                <th className="px-4 py-3 text-center text-xs font-bold text-green-900 uppercase">Qty</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-green-900 uppercase">UOM</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-green-900 uppercase">Requested By</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-green-900 uppercase">Date</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {approvedItemsList.map((item, index) => (
                                                <tr key={index} className="hover:bg-green-50 transition-colors">
                                                    <td className="px-4 py-3 text-sm font-mono font-bold text-blue-900">{item.mrNo}</td>
                                                    <td className="px-4 py-3 text-sm font-mono text-gray-700">{item.controlNo}</td>
                                                    <td className="px-4 py-3 text-sm font-bold text-blue-900">{item.itemCode}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">{item.itemDescription}</td>
                                                    <td className="px-4 py-3 text-center">
                                                        <span className="font-bold text-green-700">{item.requestedQty}</span>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-600">{item.uom}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">{item.requestedByName}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-600">
                                                        {new Date(item.mrDate).toLocaleDateString()}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className="border-t bg-gray-50 px-6 py-4 flex justify-end">
                            <button
                                onClick={() => setShowApprovedItems(false)}
                                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-all"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 🔥 IMPROVED: Item Picker Modal with debug info */}
            {/* 🔥 ITEM PICKER MODAL WITH CATEGORIES */}
            {showItemPicker && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col animate-slideUp">

                        {/* Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="bg-white/20 p-2 rounded-xl">
                                        <Package className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold">Select Items from Inventory</h3>
                                        <p className="text-blue-100 text-sm mt-1">
                                            {filteredInventoryItems.length} items available
                                            {inventoryItems.length > 0 && (
                                                <span className="ml-2">
                                                    ({inventoryItems.filter(i => i.availableQty > 0).length} in stock)
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowItemPicker(false);
                                        setSelectedCategory('All');
                                        setItemSearchText('');
                                    }}
                                    className="bg-white/10 hover:bg-white/20 rounded-xl p-2 transition-all"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                        </div>

                        {/* Search Bar */}
                        <div className="p-6 border-b bg-gray-50">
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    placeholder="Search by Item Code, Description, or Warehouse..."
                                    value={itemSearchText}
                                    onChange={(e) => {
                                        setItemSearchText(e.target.value);
                                        setItemCurrentPage(1);
                                    }}
                                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-all"
                                />
                            </div>
                        </div>

                        {/* 🔥 CATEGORY TABS */}
                        <div className="px-6 py-4 border-b bg-white overflow-x-auto">
                            <div className="flex items-center gap-2 min-w-max">
                                {(() => {
                                    // Get unique categories
                                    const allCategories = ['All', ...new Set(
                                        inventoryItems
                                            .map(item => item.category || 'Uncategorized')
                                            .filter(Boolean)
                                    )];

                                    return allCategories.map(category => {
                                        // Count items per category
                                        const count = inventoryItems.filter(item => {
                                            const itemCat = item.category || 'Uncategorized';
                                            return category === 'All' ? true : itemCat === category;
                                        }).length;

                                        return (
                                            <button
                                                key={category}
                                                onClick={() => {
                                                    setSelectedCategory(category);
                                                    setItemCurrentPage(1);
                                                }}
                                                className={`px-4 py-2 rounded-lg font-semibold whitespace-nowrap transition-all flex items-center gap-2 ${selectedCategory === category
                                                    ? 'bg-blue-600 text-white shadow-md'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    }`}
                                            >
                                                {category}
                                                <span className={`text-xs px-2 py-0.5 rounded-full ${selectedCategory === category
                                                    ? 'bg-white/20 text-white'
                                                    : 'bg-gray-200 text-gray-600'
                                                    }`}>
                                                    {count}
                                                </span>
                                            </button>
                                        );
                                    });
                                })()}
                            </div>
                        </div>

                        {/* Items List */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {filteredInventoryItems.length === 0 ? (
                                <div className="text-center py-20">
                                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500 text-lg font-medium">
                                        {inventoryItems.length === 0
                                            ? 'No inventory loaded'
                                            : itemSearchText || selectedCategory !== 'All'
                                                ? 'No items match your filters'
                                                : 'No items found'
                                        }
                                    </p>
                                    {selectedCategory !== 'All' && (
                                        <button
                                            onClick={() => setSelectedCategory('All')}
                                            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all"
                                        >
                                            Clear Category Filter
                                        </button>
                                    )}
                                    {inventoryItems.length === 0 && (
                                        <button
                                            onClick={() => {
                                                setShowItemPicker(false);
                                                retryLoadInventory();
                                            }}
                                            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 mx-auto"
                                        >
                                            <Loader2 className="w-4 h-4" />
                                            Reload Inventory
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 gap-4">
                                    {currentInventoryItems.map((item) => {
                                        const isUnavailable = item.availableQty <= 0;

                                        return (
                                            <div
                                                key={item.id}
                                                className={`border-2 rounded-xl p-5 transition-all ${isUnavailable
                                                    ? 'border-red-300 bg-red-50 opacity-75 cursor-not-allowed'
                                                    : 'border-gray-100 hover:border-blue-300 bg-white hover:shadow-md cursor-pointer group'
                                                    }`}
                                                onClick={() => {
                                                    if (!isUnavailable) handleSelectItem(item);
                                                }}
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                                                            <span className="font-mono font-bold text-lg text-blue-900">
                                                                {item.itemCode}
                                                            </span>

                                                            {/* 🔥 CATEGORY BADGE */}
                                                            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                                                                {item.category || 'Uncategorized'}
                                                            </span>

                                                            {/* Stock Badge */}
                                                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${item.availableQty > 10
                                                                ? 'bg-green-100 text-green-700'
                                                                : item.availableQty > 0
                                                                    ? 'bg-yellow-100 text-yellow-700'
                                                                    : 'bg-red-100 text-red-700'
                                                                }`}>
                                                                {item.availableQty > 0
                                                                    ? `Available: ${item.availableQty}`
                                                                    : 'Out of Stock'
                                                                } {item.uom}
                                                            </span>
                                                        </div>

                                                        <p className="text-gray-700 mb-2 font-medium">
                                                            {item.itemDescription}
                                                        </p>

                                                        <div className="flex items-center gap-4 text-sm text-gray-500">
                                                            <span className="flex items-center gap-1">
                                                                📦 <strong>{item.warehouseCode}</strong>
                                                            </span>
                                                            <span>Total: <strong>{item.quantity}</strong></span>
                                                            {item.reservedQty > 0 && (
                                                                <span className="text-amber-600">
                                                                    🔒 Reserved: <strong>{item.reservedQty}</strong>
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!isUnavailable) handleSelectItem(item);
                                                        }}
                                                        disabled={isUnavailable}
                                                        className={`ml-4 px-5 py-2.5 rounded-xl font-semibold transition-all shadow-md flex items-center gap-2 ${isUnavailable
                                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                            : 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg'
                                                            }`}
                                                    >
                                                        <Plus className="w-4 h-4" />
                                                        {isUnavailable ? 'Out of Stock' : 'Add'}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Pagination */}
                        {totalItemPages > 1 && (
                            <div className="border-t bg-gray-50 px-6 py-4 flex items-center justify-between">
                                <p className="text-sm text-gray-600">
                                    Showing {startItemIndex + 1} to {Math.min(endItemIndex, filteredInventoryItems.length)} of {filteredInventoryItems.length} items
                                    {selectedCategory !== 'All' && (
                                        <span className="ml-2 text-blue-600 font-semibold">
                                            (Category: {selectedCategory})
                                        </span>
                                    )}
                                </p>

                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setItemCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={itemCurrentPage === 1}
                                        className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-all flex items-center gap-1"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                        Previous
                                    </button>

                                    <span className="text-sm font-medium px-4">
                                        {itemCurrentPage} / {totalItemPages}
                                    </span>

                                    <button
                                        onClick={() => setItemCurrentPage(prev => Math.min(totalItemPages, prev + 1))}
                                        disabled={itemCurrentPage === totalItemPages}
                                        className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-all flex items-center gap-1"
                                    >
                                        Next
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
        </div>
    );
}